'use client';

import { SpendSegmentPicker } from '@/domains/pages/ai-spend/components/spend-segment-picker';
import { AiSpendSegmentView } from '@/domains/pages/ai-spend/ai-spend-segment-view';
import { type SpendChartMode, type SpendGroupKind } from '@/domains/pages/ai-spend/constants';
import { useAiSpendDistributionData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-distribution-data';
import { useAiSpendTaskModelData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-task-model-data';
import { useAiSpendSegmentPageData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-segment-page-data';
import { buildSegmentScopeFilter } from '@/domains/pages/ai-spend/utils/ai-spend-api-payloads';
import { pageKeyToSpendSegmentKind } from '@/domains/pages/ai-spend/utils/ai-spend-view.utils';
import { resolveSegmentSpendGroup } from '@/domains/pages/ai-spend/utils/segment-spend-group';
import { parseSpendChartMode } from '@/domains/pages/ai-spend/utils/ai-spend-over-time.utils';
import { buildNoSegmentsEmptyReason } from '@/domains/pages/ai-proficiency/utils/segment-empty-state';
import { SegmentDimensionMappingTrigger } from '@/domains/pages/shared/segment-dimension-mapping-trigger';
import { SegmentScopeSegmentControls } from '@/domains/pages/shared/segment-picker-row';
import { useSyncNebulyPageViewState } from '@/domains/pages/hooks/use-sync-nebuly-page-view-state';
import { useNebulyPageTitleSubject } from '@/domains/pages/hooks/use-nebuly-page-title-subject';
import type { NebulyPageBodyProps } from '@/domains/pages/types';
import { PARAMS_KEY_SEGMENT, PARAMS_KEY_SPEND_CHART, PARAMS_KEY_SPEND_GROUP } from '@/constants/route-params-keys';
import { useCreateQueryStringUrl } from '@/hooks/use-create-query-string-url';
import { useProjectsContext } from '@/providers/projects-provider';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
function parseSpendGroup(value: string | null): SpendGroupKind {
  if (value === 'task' || value === 'model' || value === 'team' || value === 'market' || value === 'tenure') {
    return value;
  }
  return 'task';
}
export function AiSpendSegmentPageBody({
  pageKey,
  timeRange,
  filters = [],
  specialFilters = []
}: NebulyPageBodyProps) {
  const {
    projectId
  } = useProjectsContext();
  const searchParams = useSearchParams();
  const router = useRouter();
  const createQueryStringUrl = useCreateQueryStringUrl();
  const segmentKind = pageKeyToSpendSegmentKind(pageKey);
  const isRegisteredSegmentPage = segmentKind != null;
  const spendGroup = parseSpendGroup(searchParams.get(PARAMS_KEY_SPEND_GROUP));
  const spendChart = parseSpendChartMode(searchParams.get(PARAMS_KEY_SPEND_CHART));
  const segmentFromUrl = searchParams.get(PARAMS_KEY_SEGMENT) ?? undefined;
  const segmentData = useAiSpendSegmentPageData({
    projectId,
    timeRange,
    kind: segmentKind ?? 'team',
    selectedSegmentName: segmentFromUrl,
    filters,
    specialFilters,
    enabled: isRegisteredSegmentPage
  });
  useNebulyPageTitleSubject(segmentData.selectedSegmentName ? segmentData.formatSegmentName(segmentData.selectedSegmentName) : undefined);
  const segmentScopeFilter = useMemo(() => segmentKind ? buildSegmentScopeFilter(segmentKind, segmentData.selectedSegmentName) : null, [segmentKind, segmentData.selectedSegmentName]);
  const distributionFilters = useMemo(() => segmentScopeFilter ? [...filters, segmentScopeFilter] : filters, [filters, segmentScopeFilter]);
  const distributionKind = resolveSegmentSpendGroup(spendGroup);
  const distributionTotalCost = segmentData.selected?.cost ?? null;
  const distributionData = useAiSpendDistributionData({
    projectId,
    timeRange,
    filters: distributionFilters,
    specialFilters,
    kind: distributionKind,
    totalCost: distributionTotalCost,
    enabled: isRegisteredSegmentPage
  });
  const taskModelData = useAiSpendTaskModelData({
    projectId,
    timeRange,
    filters: distributionFilters,
    specialFilters,
    enabled: isRegisteredSegmentPage
  });
  useEffect(() => {
    if (!segmentKind || segmentData.segments.length === 0 || segmentFromUrl) return;
    const defaultName = segmentData.segments[0]?.name;
    if (!defaultName) return;
    router.replace(createQueryStringUrl({
      params: {
        [PARAMS_KEY_SEGMENT]: defaultName
      }
    }));
  }, [createQueryStringUrl, router, segmentData.segments, segmentFromUrl, segmentKind]);
  const handleSelectSegment = useCallback((name: string) => {
    router.replace(createQueryStringUrl({
      params: {
        [PARAMS_KEY_SEGMENT]: name
      }
    }));
  }, [createQueryStringUrl, router]);
  const handleSpendGroupChange = useCallback((nextKind: SpendGroupKind) => {
    router.replace(createQueryStringUrl({
      params: {
        [PARAMS_KEY_SPEND_GROUP]: nextKind
      }
    }));
  }, [createQueryStringUrl, router]);
  const handleSpendChartChange = useCallback((nextMode: SpendChartMode) => {
    if (nextMode === 'total') {
      router.replace(createQueryStringUrl({
        params: {},
        toRemoveParams: [PARAMS_KEY_SPEND_CHART]
      }));
      return;
    }
    router.replace(createQueryStringUrl({
      params: {
        [PARAMS_KEY_SPEND_CHART]: nextMode
      }
    }));
  }, [createQueryStringUrl, router]);
  const isNoSegmentsEmpty = useMemo(() => !segmentData.isLoading && !segmentData.isError && segmentData.segments.length === 0, [segmentData.isError, segmentData.isLoading, segmentData.segments.length]);
  const isSegmentSpendEmpty = useMemo(() => !segmentData.isLoading && !segmentData.isError && segmentData.segments.length > 0 && (segmentData.selected?.tasks ?? 0) === 0 && (segmentData.selected?.cost == null || segmentData.selected.cost === 0), [segmentData.isError, segmentData.isLoading, segmentData.selected?.cost, segmentData.selected?.tasks, segmentData.segments.length]);
  const isEmpty = isNoSegmentsEmpty || isSegmentSpendEmpty;
  useSyncNebulyPageViewState({
    isLoading: segmentData.isLoading,
    isError: segmentData.isError,
    error: segmentData.error,
    refetch: segmentData.refetch,
    isEmpty,
    emptyReason: isNoSegmentsEmpty && segmentKind ? buildNoSegmentsEmptyReason(segmentKind) : undefined
  });
  if (!segmentKind) {
    return null;
  }
  if (segmentData.isLoading || segmentData.isError) {
    return null;
  }
  const scopeBar = segmentData.segments.length > 0 ? <div className="flex min-w-0 flex-nowrap items-center gap-2 border-b border-b-lightborder px-8 py-2">
      <SegmentScopeSegmentControls showLeadingDivider={false} picker={<SpendSegmentPicker segments={segmentData.segments} selectedName={segmentData.selectedSegmentName} onSelect={handleSelectSegment} />} trailing={<SegmentDimensionMappingTrigger segmentKind={segmentKind} />} />
    </div> : null;
  if (isEmpty) {
    return <div className="border-b border-b-lightborder" data-testid={`nebuly-page-body-${pageKey}`}>
        {scopeBar}
      </div>;
  }
  return <div className="border-b border-b-lightborder" data-testid={`nebuly-page-body-${pageKey}`}>
      {scopeBar}

      <AiSpendSegmentView pageKey={pageKey} kind={segmentKind} onSelectSegment={handleSelectSegment} timeRange={timeRange} data={segmentData} distribution={distributionData} taskModel={taskModelData} spendGroup={distributionKind} onSpendGroupChange={handleSpendGroupChange} spendChart={spendChart} onSpendChartChange={handleSpendChartChange} />
    </div>;
}